import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Papa from 'papaparse'
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Progress,
  Space,
  Table,
  Tag,
  Typography,
} from '@surecompanies/core_components'
import { Select, notification } from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  PlusOutlined,
  UploadOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { browserHistory } from 'react-router'
import { downloadJSONasCSV } from 'helpers/download-file'
import { getDefaultPaymentPeriod } from '../PaymentAssignment/hooks/usePaymentAssignmentFilters'
import {
  batchAssignByBenefitId,
  usePaymentAssignmentUsersQuery,
} from '../../api/payment-assignment-api-client'
import { Priority, PRIORITY_LABELS } from '../PaymentAssignment/Utils/types'
import type { PaymentAssignmentUserDto } from '../../api/dto/get-payment-assignment-users.dto'
import type { BatchAssignByBenefitIdResultItemDto } from '../../api/dto/batch-assign-by-benefit-id.dto'
import { PaymentAssignmentRole } from '../PaymentAssignment/Utils/roles'

const { Title, Text } = Typography

const ALLOWED_PRIORITIES = [
  Priority.EE_INQUIRY,
  Priority.HR_INQUIRY,
  Priority.EXECUTIVE_INQUIRY,
] as const

type AllowedPriority = (typeof ALLOWED_PRIORITIES)[number]

const PRIORITY_CSV_LABEL_MAP: Record<string, AllowedPriority> = {
  'ee inquiry': Priority.EE_INQUIRY,
  'hr inquiry': Priority.HR_INQUIRY,
  'executive inquiry': Priority.EXECUTIVE_INQUIRY,
  '1': Priority.EE_INQUIRY,
  '2': Priority.HR_INQUIRY,
  '3': Priority.EXECUTIVE_INQUIRY,
}

interface ParsedRow {
  key: string
  benefitId: string
  priority: AllowedPriority | ''
  assigneeName: string
  /** Resolved from assigneeName lookup; -1 means unresolved. */
  assigneeId: number
}

type Step = 'upload' | 'preview' | 'results'

interface BatchProgress {
  sent: number
  total: number
  currentBatch: number
  totalBatches: number
}

interface BulkAssignResults {
  results: BatchAssignByBenefitIdResultItemDto[]
  skippedBenefitIds: string[]
  chunkErrors: { benefitIds: string[]; message: string }[]
}

const CSV_HEADERS = ['benefitId', 'priority', 'assignee'] as const

const EXAMPLE_ROWS: Record<(typeof CSV_HEADERS)[number], string>[] = [
  {
    benefitId: 'benefit-example-id-1',
    priority: 'EE Inquiry',
    assignee: 'ACE',
  },
  {
    benefitId: 'benefit-example-id-2',
    priority: 'HR Inquiry',
    assignee: 'VIC',
  },
  {
    benefitId: 'benefit-example-id-3',
    priority: 'Executive Inquiry',
    assignee: 'Beer Prapon',
  },
  {
    benefitId: 'benefit-example-id-4',
    priority: '',
    assignee: 'Somboon Changkhom',
  },
]

const CHUNK_SIZE = 100
const MAX_CSV_ROWS = 20000

const parsePriority = (value: string): AllowedPriority | '' => {
  const normalized = value.trim().toLowerCase()
  return PRIORITY_CSV_LABEL_MAP[normalized] ?? ''
}

interface ResolvedAssignee {
  id: number
  name: string
}

/**
 * Resolves an assignee from a CSV value that can be either a display name or
 * a numeric user ID. Returns id=-1 and the original input as name when no
 * match is found.
 */
const resolveAssignee = (
  input: string,
  users: PaymentAssignmentUserDto[]
): ResolvedAssignee => {
  const trimmed = input.trim()
  if (!trimmed) return { id: -1, name: '' }

  // Try matching by numeric ID first
  const numericId = Number(trimmed)
  if (!isNaN(numericId) && Number.isInteger(numericId) && numericId > 0) {
    const byId = users.find((u) => u.id === numericId)
    if (byId) return { id: byId.id, name: byId.name }
  }

  // Fall back to case-insensitive name match
  const normalized = trimmed.toLowerCase()
  const byName = users.find((u) => u.name.trim().toLowerCase() === normalized)
  if (byName) return { id: byName.id, name: byName.name }

  return { id: -1, name: trimmed }
}

const getRowErrors = (row: ParsedRow): string[] => {
  const errors: string[] = []
  if (!row.benefitId.trim()) errors.push('Benefit ID is required')
  if (!row.assigneeName.trim()) {
    errors.push('Assignee is required')
  } else if (row.assigneeId === -1) {
    errors.push(
      `Assignee "${row.assigneeName}" not found — enter a valid name or user ID`
    )
  }
  return errors
}

const isValidRow = (row: ParsedRow): boolean => getRowErrors(row).length === 0

export default function PaymentAssignmentBulkAssignUpload() {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [step, setStep] = useState<Step>('upload')
  const [period, setPeriod] = useState<Dayjs>(() =>
    dayjs(getDefaultPaymentPeriod(), 'YYYY-MM').startOf('month')
  )
  const [rows, setRows] = useState<ParsedRow[]>([])
  const [parseError, setParseError] = useState<string | null>(null)
  const [results, setResults] = useState<BulkAssignResults | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const cancelledRef = useRef(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const isSubmittingRef = useRef(false)
  const [batchProgress, setBatchProgress] = useState<BatchProgress>({
    sent: 0,
    total: 0,
    currentBatch: 0,
    totalBatches: 0,
  })

  useEffect(() => {
    isSubmittingRef.current = isSubmitting
  }, [isSubmitting])

  useEffect(() => {
    if (!isSubmitting) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isSubmitting])

  useEffect(() => {
    const unlisten = browserHistory.listenBefore(() => {
      if (!isSubmittingRef.current) return
      const confirmed = window.confirm(
        'Bulk assign is still in progress. Leaving will cancel the remaining batches. Are you sure?'
      )
      if (!confirmed) return false
      cancelledRef.current = true
      abortControllerRef.current?.abort()
    })
    return () => {
      unlisten()
      if (isSubmittingRef.current) {
        cancelledRef.current = true
        abortControllerRef.current?.abort()
      }
    }
  }, [])

  const { data: makersData, isLoading: makersLoading } =
    usePaymentAssignmentUsersQuery({ roles: [PaymentAssignmentRole.MAKER] })
  const { data: escalatorsData, isLoading: escalatorsLoading } =
    usePaymentAssignmentUsersQuery({ roles: [PaymentAssignmentRole.ESCALATOR] })

  const allUsers = useMemo(() => {
    const makers = makersData?.items ?? []
    const escalators = escalatorsData?.items ?? []
    const seen = new Set<number>()
    const merged: PaymentAssignmentUserDto[] = []
    for (const u of [...makers, ...escalators]) {
      if (!seen.has(u.id)) {
        seen.add(u.id)
        merged.push(u)
      }
    }
    return merged
  }, [makersData?.items, escalatorsData?.items])

  const usersLoading = makersLoading || escalatorsLoading

  const nextRowKeyRef = useRef(0)
  const allocateRowKey = useCallback(() => {
    const id = nextRowKeyRef.current
    nextRowKeyRef.current = id + 1
    return `r-${id}`
  }, [])
  const makeEmptyRow = useCallback(
    (): ParsedRow => ({
      key: allocateRowKey(),
      benefitId: '',
      priority: '',
      assigneeName: '',
      assigneeId: -1,
    }),
    [allocateRowKey]
  )

  const handleDownloadTemplate = () => {
    downloadJSONasCSV(
      EXAMPLE_ROWS,
      'payment-assignment-bulk-assign-template.csv'
    )
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setParseError(null)

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        if (result.errors.length > 0) {
          setParseError(
            `CSV parse error: ${result.errors.map((err) => err.message).join(', ')}`
          )
          return
        }

        const parsed: ParsedRow[] = result.data.map((rawRow) => {
          const assigneeInput = rawRow['assignee']?.trim() ?? ''
          const resolved = resolveAssignee(assigneeInput, allUsers)
          return {
            key: allocateRowKey(),
            benefitId: rawRow['benefitId']?.trim() ?? '',
            priority: parsePriority(rawRow['priority'] ?? ''),
            assigneeName: resolved.name,
            assigneeId: resolved.id,
          }
        })

        if (parsed.length === 0) {
          setParseError('CSV has no data rows.')
          return
        }
        if (parsed.length > MAX_CSV_ROWS) {
          setParseError(
            `CSV contains ${parsed.length} rows. Maximum allowed is ${MAX_CSV_ROWS.toLocaleString()}.`
          )
          return
        }

        parsed.sort((a, b) => {
          const nameCmp = a.assigneeName.localeCompare(b.assigneeName)
          if (nameCmp !== 0) return nameCmp
          const priA = a.priority === '' ? 0 : a.priority
          const priB = b.priority === '' ? 0 : b.priority
          return priA - priB
        })

        setRows(parsed)
        setStep('preview')
      },
      error: (err: Error) => {
        setParseError(`Failed to read file: ${err.message}`)
      },
    })

    e.target.value = ''
  }

  const handleCellChange = useCallback(
    (key: string, field: keyof ParsedRow, value: string) => {
      setRows((curr) =>
        curr.map((r) => {
          if (r.key !== key) return r
          const updated = { ...r, [field]: value }
          if (field === 'assigneeName') {
            const resolved = resolveAssignee(value, allUsers)
            updated.assigneeName = resolved.name
            updated.assigneeId = resolved.id
          }
          return updated
        })
      )
    },
    [allUsers]
  )

  const handleAddRow = useCallback(() => {
    setRows((curr) => [...curr, makeEmptyRow()])
  }, [makeEmptyRow])

  const handleDeleteRow = useCallback((key: string) => {
    setRows((curr) => curr.filter((r) => r.key !== key))
  }, [])

  const handleAddManually = useCallback(() => {
    setParseError(null)
    setRows([makeEmptyRow()])
    setStep('preview')
  }, [makeEmptyRow])

  const handleConfirm = useCallback(async () => {
    const validRows = rows.filter(isValidRow)
    const items = validRows.map((row) => ({
      benefitId: row.benefitId,
      assigneeId: row.assigneeId,
      priority:
        row.priority === ''
          ? Priority.DEFAULT
          : (row.priority as AllowedPriority),
    }))

    const totalBatches = Math.ceil(items.length / CHUNK_SIZE)

    const abortController = new AbortController()
    abortControllerRef.current = abortController
    cancelledRef.current = false
    setIsCancelling(false)
    setIsSubmitting(true)
    setBatchProgress({
      sent: 0,
      total: items.length,
      currentBatch: 1,
      totalBatches,
    })

    const allResults: BatchAssignByBenefitIdResultItemDto[] = []
    const allSkipped: string[] = []
    const chunkErrors: BulkAssignResults['chunkErrors'] = []

    for (let i = 0; i < items.length; i += CHUNK_SIZE) {
      if (cancelledRef.current) break

      const batchNum = Math.floor(i / CHUNK_SIZE) + 1
      setBatchProgress((prev) => ({ ...prev, currentBatch: batchNum }))

      const chunk = items.slice(i, i + CHUNK_SIZE)
      try {
        const response = await batchAssignByBenefitId({
          month: period.month(),
          year: period.year(),
          items: chunk,
        })

        allResults.push(...response)

        const returnedCount = response.length
        if (returnedCount < chunk.length) {
          allSkipped.push(
            ...chunk.slice(returnedCount).map((item) => item.benefitId)
          )
        }
      } catch (error: any) {
        if (cancelledRef.current || abortController.signal.aborted) break
        const raw =
          error.response?.data?.message ||
          error.message ||
          'Batch request failed'
        const msg = typeof raw === 'string' ? raw : JSON.stringify(raw)
        chunkErrors.push({
          benefitIds: chunk.map((item) => item.benefitId),
          message: msg,
        })
      }

      setBatchProgress((prev) => ({
        ...prev,
        sent: Math.min(i + CHUNK_SIZE, items.length),
      }))
    }

    abortControllerRef.current = null

    if (
      cancelledRef.current &&
      allResults.length === 0 &&
      chunkErrors.length === 0
    ) {
      setIsSubmitting(false)
      setIsCancelling(false)
      notification.info({ message: 'Bulk assign cancelled' })
      return
    }

    setIsSubmitting(false)
    setResults({
      results: allResults,
      skippedBenefitIds: allSkipped,
      chunkErrors,
    })
    setStep('results')

    const successCount = allResults.filter((r) => r.success).length
    const failureCount = allResults.length - successCount
    if (successCount > 0) {
      notification.success({
        message: `Assigned ${successCount} payment assignment(s)`,
      })
    }
    if (failureCount > 0 || chunkErrors.length > 0) {
      notification.warning({
        message: `${failureCount + chunkErrors.reduce((sum, c) => sum + c.benefitIds.length, 0)} item(s) failed to assign`,
      })
    }
  }, [rows, period])

  const handleReset = () => {
    cancelledRef.current = true
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
    setIsSubmitting(false)
    setIsCancelling(false)
    setStep('upload')
    setRows([])
    setResults(null)
    setParseError(null)
    setBatchProgress({ sent: 0, total: 0, currentBatch: 0, totalBatches: 0 })
  }

  const validCount = useMemo(() => rows.filter(isValidRow).length, [rows])
  const invalidCount = rows.length - validCount

  const assigneeOptions = useMemo(
    () =>
      allUsers.map((u) => ({
        value: u.name,
        label: `${u.name} (${u.role ?? 'unknown'})`,
      })),
    [allUsers]
  )

  const previewColumns = [
    {
      title: 'Status',
      key: 'status_icon',
      width: 70,
      render: (_: unknown, row: ParsedRow) => {
        const errors = getRowErrors(row)
        return errors.length === 0 ? (
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
        ) : (
          <CloseCircleOutlined
            style={{ color: '#ff4d4f', fontSize: 16 }}
            title={errors.join('\n')}
          />
        )
      },
    },
    {
      title: 'Benefit ID',
      dataIndex: 'benefitId',
      key: 'benefitId',
      width: 280,
      render: (value: string, row: ParsedRow) => (
        <input
          style={{
            border: '1px solid #d9d9d9',
            borderRadius: 4,
            padding: '2px 6px',
            width: '100%',
            fontSize: 13,
          }}
          value={value}
          onChange={(e) =>
            handleCellChange(row.key, 'benefitId', e.target.value)
          }
        />
      ),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 200,
      render: (value: AllowedPriority | '', row: ParsedRow) => (
        <Select
          size="small"
          style={{ width: '100%' }}
          value={value === '' ? '' : String(value)}
          onChange={(v: string) => handleCellChange(row.key, 'priority', v)}
          options={[
            { value: '', label: '-' },
            ...ALLOWED_PRIORITIES.map((p) => ({
              value: String(p),
              label: PRIORITY_LABELS[p],
            })),
          ]}
        />
      ),
    },
    {
      title: 'Assignee Name',
      dataIndex: 'assigneeName',
      key: 'assigneeName',
      width: 260,
      render: (value: string, row: ParsedRow) => (
        <Select
          size="small"
          style={{ width: '100%' }}
          showSearch
          value={value || undefined}
          placeholder="Select assignee"
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          onChange={(v: string) => handleCellChange(row.key, 'assigneeName', v)}
          options={assigneeOptions}
          loading={usersLoading}
          notFoundContent={usersLoading ? 'Loading users...' : 'No users found'}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      align: 'center' as const,
      render: (_: unknown, row: ParsedRow) => (
        <Button
          size="small"
          type="text"
          danger
          icon={<DeleteOutlined />}
          disabled={isSubmitting}
          onClick={() => handleDeleteRow(row.key)}
          aria-label="Remove row"
        />
      ),
    },
  ]

  // Upload step
  if (step === 'upload') {
    return (
      <Card style={{ padding: '24px' }}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Title level={4} style={{ margin: 0 }}>
            Bulk Assign Payment Assignments
          </Title>
          <Text type="secondary">
            Upload a CSV of benefit IDs to bulk assign payment assignments to
            makers/escalators with a specified priority for the selected payment
            period. Assignee names are matched against the maker and escalator
            user list.
          </Text>

          {parseError && (
            <Alert type="error" message={parseError} showIcon closable />
          )}

          {usersLoading && (
            <Alert
              type="info"
              message="Loading maker and escalator user lists..."
              showIcon
            />
          )}

          <Space wrap align="center">
            <Text strong>Payment period:</Text>
            <DatePicker
              picker="month"
              value={period}
              onChange={(value: Dayjs | null) => {
                if (value) setPeriod(value.startOf('month'))
              }}
              format="MMMM YYYY"
              allowClear={false}
              style={{ minWidth: 180 }}
            />
          </Space>

          <Space wrap>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
            >
              Download CSV Template
            </Button>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => fileInputRef.current?.click()}
              disabled={usersLoading}
            >
              Upload CSV
            </Button>

            <Button
              icon={<PlusOutlined />}
              onClick={handleAddManually}
              disabled={usersLoading}
            >
              Add Manually
            </Button>
          </Space>

          <Card
            size="small"
            style={{ background: '#fafafa', borderStyle: 'dashed' }}
          >
            <Text strong>CSV format — required columns:</Text>
            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
              <li>
                <Text code>benefitId</Text>
                <Text type="secondary"> — required</Text>
              </li>
              <li>
                <Text code>priority</Text>
                <Text type="secondary">
                  {' '}
                  — optional: EE Inquiry, HR Inquiry, or Executive Inquiry
                  (leave empty for no priority)
                </Text>
              </li>
              <li>
                <Text code>assignee</Text>
                <Text type="secondary">
                  {' '}
                  — required: maker/escalator name or numeric user ID
                </Text>
              </li>
            </ul>
            <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              Submitted in batches of {CHUNK_SIZE} per request. Maximum{' '}
              {MAX_CSV_ROWS.toLocaleString()} rows per file.
            </Text>
          </Card>
        </Space>
      </Card>
    )
  }

  // Preview step
  if (step === 'preview') {
    return (
      <Card style={{ padding: '24px' }}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Space style={{ justifyContent: 'space-between', width: '100%' }}>
            <Title level={4} style={{ margin: 0 }}>
              Preview &amp; Edit
            </Title>
            <Button onClick={handleReset} disabled={isSubmitting}>
              Back to Upload
            </Button>
          </Space>

          <Space
            style={{ justifyContent: 'space-between', width: '100%' }}
            wrap
          >
            <Space wrap>
              <Tag color="blue">Period: {period.format('MMMM YYYY')}</Tag>
              <Tag color="success">
                <CheckCircleOutlined /> {validCount} valid
              </Tag>
              {invalidCount > 0 && (
                <Tag color="error">
                  <CloseCircleOutlined /> {invalidCount} invalid (will be
                  skipped)
                </Tag>
              )}
            </Space>
            <Button
              icon={<PlusOutlined />}
              onClick={handleAddRow}
              disabled={isSubmitting}
            >
              Add Row
            </Button>
          </Space>

          {invalidCount > 0 && (
            <Alert
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              message={`${invalidCount} row(s) have validation errors and will be skipped. Fix the highlighted rows or they will not be submitted.`}
            />
          )}

          {validCount === 0 && (
            <Alert
              type="error"
              showIcon
              message={
                rows.length === 0
                  ? 'No rows yet. Click "Add Row" to enter data, or go back to upload a CSV.'
                  : 'No valid rows to submit. Please fix the errors above.'
              }
            />
          )}

          <Table
            dataSource={rows}
            columns={previewColumns}
            rowKey="key"
            size="small"
            scroll={{ x: 900 }}
            pagination={{ pageSize: 50, showSizeChanger: false }}
            rowClassName={(row) =>
              isValidRow(row) ? '' : 'ant-table-row-error'
            }
          />

          <style>{`
            .ant-table-row-error td {
              background-color: #fff2f0 !important;
            }
          `}</style>

          {isSubmitting && batchProgress.total > 0 && (
            <>
              <Text type="secondary">
                {isCancelling
                  ? 'Cancelling… waiting for current batch to finish'
                  : `Sending batch ${batchProgress.currentBatch} of ${batchProgress.totalBatches} (${CHUNK_SIZE} items per batch)…`}
              </Text>
              <Progress
                percent={Math.round(
                  (batchProgress.sent / batchProgress.total) * 100
                )}
                format={() =>
                  `${batchProgress.sent} / ${batchProgress.total} rows`
                }
                status={isCancelling ? 'exception' : 'active'}
              />
            </>
          )}

          <Space>
            {isSubmitting ? (
              <Button
                danger
                disabled={isCancelling}
                onClick={() => {
                  cancelledRef.current = true
                  abortControllerRef.current?.abort()
                  setIsCancelling(true)
                }}
              >
                {isCancelling ? 'Cancelling…' : 'Cancel Upload'}
              </Button>
            ) : (
              <Button
                type="primary"
                disabled={validCount === 0}
                onClick={handleConfirm}
              >
                Confirm &amp; Assign {validCount} Benefit(s)
              </Button>
            )}
            <Button onClick={handleReset} disabled={isSubmitting}>
              Cancel
            </Button>
          </Space>
        </Space>
      </Card>
    )
  }

  // Results step
  const successList = (results?.results ?? []).filter((r) => r.success)
  const failureList = (results?.results ?? []).filter((r) => !r.success)
  const chunkErrorRows = (results?.chunkErrors ?? []).flatMap((c) =>
    c.benefitIds.map((benefitId) => ({ benefitId, message: c.message }))
  )
  const skippedRows = (results?.skippedBenefitIds ?? []).map((benefitId) => ({
    benefitId,
  }))

  const successColumns = [
    {
      title: 'Benefit ID',
      dataIndex: 'benefitId',
      key: 'benefitId',
      render: (id: string) => <Text code>{id}</Text>,
    },
  ]

  const failureColumns = [
    {
      title: 'Benefit ID',
      dataIndex: 'benefitId',
      key: 'benefitId',
      width: 300,
      render: (id: string) => <Text code>{id}</Text>,
    },
    { title: 'Error', dataIndex: 'error', key: 'error' },
  ]

  const chunkErrorColumns = [
    { title: 'Benefit ID', dataIndex: 'benefitId', key: 'benefitId' },
    { title: 'Error', dataIndex: 'message', key: 'message' },
  ]

  const skippedColumns = [
    { title: 'Benefit ID', dataIndex: 'benefitId', key: 'benefitId' },
  ]

  return (
    <Card style={{ padding: '24px' }}>
      <Space direction="vertical" size={24} style={{ width: '100%' }}>
        <Space style={{ justifyContent: 'space-between', width: '100%' }}>
          <Title level={4} style={{ margin: 0 }}>
            Bulk Assign Results — {period.format('MMMM YYYY')}
          </Title>
          <Button type="primary" onClick={handleReset}>
            Start New Batch
          </Button>
        </Space>

        {successList.length > 0 && (
          <Alert
            type="success"
            showIcon
            message={`${successList.length} benefit(s) assigned successfully`}
          />
        )}

        {(failureList.length > 0 || chunkErrorRows.length > 0) && (
          <Alert
            type="error"
            showIcon
            message={`${failureList.length + chunkErrorRows.length} item(s) failed to assign`}
          />
        )}

        {skippedRows.length > 0 && (
          <Alert
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            message={`${skippedRows.length} benefit ID(s) had no matching payment assignment for ${period.format('MMMM YYYY')} and were skipped.`}
          />
        )}

        {successList.length > 0 && (
          <Card
            size="small"
            title={
              <Text strong>
                <CheckCircleOutlined style={{ color: '#52c41a' }} /> Assigned (
                {successList.length})
              </Text>
            }
          >
            <Table
              dataSource={successList}
              columns={successColumns}
              rowKey="benefitId"
              size="small"
              pagination={{ pageSize: 25, showSizeChanger: false }}
            />
          </Card>
        )}

        {failureList.length > 0 && (
          <Card
            size="small"
            title={
              <Text strong>
                <CloseCircleOutlined style={{ color: '#ff4d4f' }} /> Failures (
                {failureList.length})
              </Text>
            }
          >
            <Table
              dataSource={failureList}
              columns={failureColumns}
              rowKey="benefitId"
              size="small"
              pagination={{ pageSize: 25, showSizeChanger: false }}
            />
          </Card>
        )}

        {chunkErrorRows.length > 0 && (
          <Card
            size="small"
            title={
              <Text strong>
                <CloseCircleOutlined style={{ color: '#ff4d4f' }} /> Batch
                Request Errors ({chunkErrorRows.length})
              </Text>
            }
          >
            <Table
              dataSource={chunkErrorRows}
              columns={chunkErrorColumns}
              rowKey="benefitId"
              size="small"
              pagination={{ pageSize: 25, showSizeChanger: false }}
            />
          </Card>
        )}

        {skippedRows.length > 0 && (
          <Card
            size="small"
            title={
              <Text strong>
                <WarningOutlined style={{ color: '#faad14' }} /> Skipped — no
                payment assignment found ({skippedRows.length})
              </Text>
            }
          >
            <Table
              dataSource={skippedRows}
              columns={skippedColumns}
              rowKey="benefitId"
              size="small"
              pagination={{ pageSize: 25, showSizeChanger: false }}
            />
          </Card>
        )}
      </Space>
    </Card>
  )
}
